import { useEffect, useLayoutEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import classNames from 'classnames';

import { CallStatus } from '../../../../lib/enums';
import { StockExchangeCodeType } from '../../../../lib/models/fundamental/types';
import { GWMarketDataSummaryBucket } from '../../../../lib/models/gateway/types';
import { MarketState } from '../../../../lib/models/market-data/types';
import { FxMDSSubscriptionService } from '../../../../lib/services/subscription/FxMDSSubscription.service';
import { MDSSubscriptionService } from '../../../../lib/services/subscription/MDSSubscription.service';
import { RootState } from '../../../../lib/store';
import { ChartDataRange } from '../../../../lib/store/market-data/charting/types';
import { PaymentCall } from '../../../../lib/store/payment/types';
import { isSymbolETF as isETF, isUserVerifiedStatus } from '../../../../lib/store/selectors';
import BigChartCache from '../../../../lib/store-util/BigChartCache';
import MarketStateCache from '../../../../lib/store-util/MarketStateCache';
import { isFxSymbol } from '../../../../lib/util/CurrencyHelpers';
import { chartingRangeToGatewaySummaryBucket } from '../../../../lib/util/GatewayHelpers';
import { useInterval } from '../../../../lib/util/hooks/TimeoutHooks';

import ScopeButtons from '../../../../components/Chart/ScopeButtons/ScopeButtons';
import { ChartContainer } from '../../../../components/Chart/SymbolChart/ChartContainer';
import TradeViewChartButton from '../../../../components/Chart/TradingViewChartButton/TradeViewChartButton';
import CompanyDetails from '../../../../components/SymbolProfile/CompanyDetails/CompanyDetails';
import KeyStatistics from '../../../../components/SymbolProfile/KeyStatistics/KeyStatistics';
import PendingOrders from '../../../../components/SymbolProfile/PendingOrders/PendingOrders';
import PeopleAlsoTrade from '../../../../components/SymbolProfile/PeopleAlsoTrade/PeopleAlsoTrade';
import SharesOwned from '../../../../components/SymbolProfile/SharesOwned/SharesOwned';
import { useIsMobile } from '../../../../hooks';

import styles from './Overview.module.scss';

type Props = {
  symbol: string;
  renderSellAndBuyButtons: () => JSX.Element;
} & ReduxProps;

const Overview = (props: Props) => {
  const {
    symbol,
    renderSellAndBuyButtons,
    hasDeposit,
    individualData,
    isPaymentsReady,
    isSymbolETF,
  } = props;

  const { isMobile } = useIsMobile();
  const isUserVerified = isUserVerifiedStatus(individualData);

  const [currentRange, setCurrentRange] = useState<ChartDataRange>('1D');
  const [showNoData, setShowNoData] = useState<boolean>(false);
  const [counter, setCounter] = useState<number>(0);
  const [hasChangedSymbol, setHasChangedSymbol] = useState<boolean>(false);
  const [chartWrapperHeight, setChartWrapperHeight] = useState<number>(0);

  const { dataByRange: chartDataByRange } = BigChartCache.use();
  const dataByRange = currentRange && chartDataByRange ? chartDataByRange[currentRange] : undefined;
  const isMarketOpen = MarketStateCache.get().marketState === MarketState.RegularHours;
  const mdsSubscriptionService = MDSSubscriptionService.getInstance();
  const fxMdsSubscriptionService = FxMDSSubscriptionService.getInstance();

  useEffect(() => {
    if (dataByRange?.isValid) {
      setCounter(0);
      setHasChangedSymbol(false);
    }
  }, [dataByRange?.isValid]);

  useEffect(() => {
    if (!hasChangedSymbol) {
      const bucket = chartingRangeToGatewaySummaryBucket(currentRange);
      if (isFxSymbol(symbol)) {
        fxMdsSubscriptionService.subscribe(symbol, bucket, StockExchangeCodeType.US_NASDAQ, true);
      } else {
        mdsSubscriptionService.subscribe(symbol, bucket, StockExchangeCodeType.US_NASDAQ, true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRange]);

  useInterval(() => {
    if (symbol && counter < 10) {
      if (isFxSymbol(symbol)) {
        fxMdsSubscriptionService.subscribe(
          symbol,
          GWMarketDataSummaryBucket.OneDay,
          StockExchangeCodeType.US_NASDAQ,
          true,
        );
      } else {
        mdsSubscriptionService.subscribe(
          symbol,
          GWMarketDataSummaryBucket.OneDay,
          StockExchangeCodeType.US_NASDAQ,
          true,
        );
      }

      if (!dataByRange?.isValid) {
        setCounter(counter + 1);
      }
    }
    // eslint-disable-next-line no-nested-ternary
  }, currentRange === '1D' && isMarketOpen ? (!dataByRange?.isValid ? 1000 : 5000) : null);

  useLayoutEffect(() => {
    function updateSize() {
      const windowWitdth = document.getElementById('charContainer')?.offsetWidth;
      if (windowWitdth) {
        setChartWrapperHeight(windowWitdth / 3);
      }
    }
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const handleBtnRetry = () => {
    setShowNoData(false);
    if (isFxSymbol(symbol)) {
      fxMdsSubscriptionService.subscribe(
        symbol,
        chartingRangeToGatewaySummaryBucket(currentRange),
        StockExchangeCodeType.US_NASDAQ,
        true,
      );
    } else {
      mdsSubscriptionService.subscribe(
        symbol,
        chartingRangeToGatewaySummaryBucket(currentRange),
        StockExchangeCodeType.US_NASDAQ,
        true,
      );
    }
  };

  return (
    <div>
      <div
        id="charContainer"
        className={classNames(styles.chartContainer, { [styles.chartContainerMob]: isMobile })}
        style={{ minHeight: `${(chartWrapperHeight) * 1.5}px` }}
      >
        <div style={{ height: `${isMobile ? chartWrapperHeight * 1.7 : chartWrapperHeight}px` }}>
          {(dataByRange?.isValid || showNoData) && (
            <ChartContainer
              symbol={symbol}
              dataSet={dataByRange!}
              range={currentRange}
              hasNoData={showNoData}
              handleBtnRetry={handleBtnRetry}
              isEuroStock={false}
              isCrypto={false}
            />
          )}
        </div>
        <div className={isMobile ? styles.scopeButtonsWrapperMobile : styles.scopeButtonsWrapper}>
          <ScopeButtons
            symbol={symbol}
            chartDataByRange={chartDataByRange}
            changeRange={(range: ChartDataRange) => {
              setCurrentRange(range);
            }}
            onScopeButtonClick={() => {
              if (hasChangedSymbol) setHasChangedSymbol(!hasChangedSymbol);
            }}
          />
          <TradeViewChartButton symbol={symbol!} />
        </div>
      </div>
      {isMobile && hasDeposit && isUserVerified && isPaymentsReady && renderSellAndBuyButtons()}
      <div className={isMobile ? styles.sectionMobile : styles.section}>
        <SharesOwned symbol={symbol} />
      </div>
      <div className={isMobile ? styles.sectionMobile : styles.section}>
        <PendingOrders symbol={symbol} />
      </div>
      <div className={isMobile ? styles.sectionMobile : styles.section}>
        <KeyStatistics symbol={symbol} />
      </div>
      {!isSymbolETF(symbol) && (
        <div className={isMobile ? styles.sectionMobile : styles.section}>
          <CompanyDetails symbol={symbol} />
        </div>
      )}
      {isUserVerified && (
        <div className={isMobile ? styles.sectionMobile : styles.section}>
          <PeopleAlsoTrade symbol={symbol} />
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  hasDeposit: state.payment.hasDeposit,
  individualData: state.crm.individualExtendedInfo,
  isSymbolETF: (symbol: string) => isETF(symbol, state),
  isPaymentsReady: state.payment.statusByCall[PaymentCall.getPaymentsAll]?.status === CallStatus.READY,
});

const connector = connect(mapStateToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(Overview);
